<template>
    <div class="app-pagination" :class="{ 'is-centered': centered, 'is-mobile': isMobile }">

        <div class="text-muted app-pagination-summary" v-if="!centered || isMobile">
            <template v-if="meta.total">
                <span class="fw-semibold">{{ formatNumber(meta.from) }} à {{ formatNumber(meta.to) }}</span> sur <span class="fw-semibold">{{ formatNumber(meta.total) }}</span><span class="d-none d-md-inline"> résultats</span>
            </template>
        </div>

        <nav v-if="visible">
            <ul class="pagination pagination-separated mb-0">
                <li class="page-item" :class="{ 'disabled': links.prev === null }">
                    <a class="page-link" href="#" aria-label="Première page" @click.prevent="changePage(links.first)">
                        <i class="mdi mdi-chevron-double-left"></i>
                    </a>
                </li>
                <li class="page-item" :class="{ 'disabled': links.prev === null }">
                    <a class="page-link" href="#" aria-label="Page précédente" @click.prevent="changePage(links.prev)">
                        <i class="mdi mdi-chevron-left"></i>
                    </a>
                </li>

                <li class="page-item" v-for="(link, index) in pageLinks" :key="index + link.label" :class="{ 'active': link.active, 'disabled': link.url === null }">
                    <a href="#" class="page-link" @click.prevent="changePage(link.url)" v-html="link.label"></a>
                </li>

                <li class="page-item" :class="{ 'disabled': links.next === null }">
                    <a class="page-link" href="#" aria-label="Page suivante" @click.prevent="changePage(links.next)">
                        <i class="mdi mdi-chevron-right"></i>
                    </a>
                </li>
                <li class="page-item" :class="{ 'disabled': links.next === null }">
                    <a class="page-link" href="#" aria-label="Dernière page" @click.prevent="changePage(links.last)">
                        <i class="mdi mdi-chevron-double-right"></i>
                    </a>
                </li>
            </ul>
        </nav>
    </div>

</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { formatNumber } from '../Utils/numberFormat.js'

const props = defineProps({
    visible: { type: Boolean, default: false },
    links: { type: Object, required: true },
    meta: { type: Object, required: true },
    // Pagination seule, centrée (grilles d'actualités, de fiches d'aide, de médias)
    centered: { type: Boolean, default: false },
})

const emit = defineEmits(['change-page:clicked'])

const MOBILE_MAX_WIDTH = 767.98

const isMobile = ref(window.innerWidth <= MOBILE_MAX_WIDTH)

const handleResize = () => {
    isMobile.value = window.innerWidth <= MOBILE_MAX_WIDTH
}

// Numéros de page (sans « Précédent » / « Suivant » fournis par Laravel) ; sur téléphone,
// seulement la page courante et ses deux voisines, sans les « … »
const pageLinks = computed(() => {
    const numbers = (props.meta.links ?? []).slice(1, -1)

    if (!isMobile.value) {
        return numbers
    }

    const pages = numbers.filter((link) => link.url !== null || link.active)
    const current = pages.findIndex((link) => link.active)
    const start = Math.max(0, Math.min(current - 1, pages.length - 3))

    return pages.slice(start, start + 3)
})

const changePage = (link) => {
    if (link !== null) {
        emit('change-page:clicked', link)
    }
}

onMounted(() => {
    window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.app-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.app-pagination.is-centered {
    justify-content: center;
}

/* Téléphone : pagination centrée, résumé dessous, boutons faciles à toucher */
.app-pagination.is-mobile {
    flex-direction: column-reverse;
    justify-content: center;
    gap: 0.5rem;
}

.app-pagination.is-mobile .pagination {
    flex-wrap: nowrap;
    justify-content: center;
}

.app-pagination.is-mobile .page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2.4rem;
    height: 2.4rem;
    padding: 0 0.5rem;
    font-size: 0.875rem;
}

.app-pagination.is-mobile .page-item:first-child .page-link {
    margin-left: 0;
}

.app-pagination.is-mobile .app-pagination-summary {
    font-size: 0.75rem;
}
</style>
